import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { App, Button, List, Space, Spin, Tag, Typography, Alert } from 'antd';
import PortalLayout from './PortalLayout';
import PortalErrorPage from './PortalErrorPage';
import { portalApi, PortalInvoiceView } from '../../services/api/portalApi';
import { PortalDVISection } from '../../components/DVI';
import { displayedFeesTotal } from '../../utils/estimateTotals';

const { Text } = Typography;

const PortalInvoicePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const [data, setData] = useState<PortalInvoiceView | null>(null);
  const [error, setError] = useState<'not_found' | 'rate_limited' | 'unknown' | null>(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!token) return;
    portalApi.getInvoice(token).then(setData).catch((err) => {
      const status = err?.response?.status;
      setError(status === 404 ? 'not_found' : status === 429 ? 'rate_limited' : 'unknown');
    });
  }, [token]);

  if (error) return <PortalErrorPage variant={error} />;
  if (!data) {
    return (
      <PortalLayout title="Loading…" shopName="CNF Auto">
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      </PortalLayout>
    );
  }

  const inv = data.invoice;
  const isPaid = inv.status === 'paid' || (inv.amount_paid ?? 0) >= (inv.total_amount ?? 0);
  /**
   * WP-10 slice 4 — did this visit arrive back from Stripe's page?
   *
   * Used ONLY to choose the wording while the webhook is still in flight. It is
   * never evidence of payment: `isPaid` above, derived from the server payload,
   * is the only thing that can show a confirmation.
   */
  const returningFromCheckout = searchParams.get('paid') === '1';
  const amountDue = Math.max((inv.total_amount ?? 0) - (inv.amount_paid ?? 0), 0);
  // Prefer what was actually charged per fee; fall back to the stored-totals
  // remainder for invoices snapshotted before `computed_amount` was carried.
  // Summing those as zero printed "$0.00" beside a total that included the fee.
  const feesTotal = displayedFeesTotal(data.fees as any, inv as any);

  const onPay = async () => {
    if (!token) return;
    setPaying(true);
    try {
      const { checkout_url } = await portalApi.createCheckoutSession(token);
      window.location.href = checkout_url;
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Could not start checkout');
      setPaying(false);
    }
  };

  return (
    <PortalLayout
      title={inv.invoice_number}
      shopName="CNF Auto"
      subtitle={`${data.vehicle.year ?? ''} ${data.vehicle.make ?? ''} ${data.vehicle.model ?? ''}`.trim()}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/*
          WP-10 slice 4 — "Payment received" now means the SERVER says so.

          This banner used to render on `?paid=1` alone, which is a claim by
          whoever built the URL and not evidence of anything. Two people saw a
          confirmation that was not true: anyone handed a crafted link, and — far
          more commonly — a customer who opened Stripe's page, changed their
          mind, and pressed Back, arriving on the success URL without paying.

          `isPaid` is derived from `invoice.status` / `amount_paid` on the
          payload, which the webhook writes. So the banner appears only once the
          money is actually recorded.

          The query parameter is still read, but only to decide whether this
          visit is a RETURN FROM CHECKOUT, which changes what to say while the
          webhook is still in flight — "we're confirming" is honest, "payment
          received" would not be.
        */}
        {isPaid ? (
          <Alert type="success" message="Payment received — thanks!" showIcon />
        ) : returningFromCheckout ? (
          <Alert
            type="info"
            showIcon
            message="Confirming your payment…"
            description="This can take a few seconds. Refresh if it doesn't update."
          />
        ) : null}
        <Space>
          <Text>Status:</Text>
          <Tag color={isPaid ? 'green' : 'gold'}>{isPaid ? 'PAID' : (inv.status ?? '').toUpperCase()}</Tag>
        </Space>
        <List
          bordered
          dataSource={data.line_items}
          renderItem={(item: any) => (
            <List.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text>{item.description}</Text>
                <Text strong>${(item.total_price ?? 0).toFixed(2)}</Text>
              </Space>
            </List.Item>
          )}
        />
        {(data.fees ?? []).length > 0 && (
          <List
            size="small"
            bordered
            dataSource={data.fees ?? []}
            renderItem={(fee: any) => (
              <List.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text type="secondary">{fee.label}</Text>
                  {/* No invented figure: an older row with no computed amount
                      shows the rule it was billed under, and the Fees line below
                      carries the dollars. */}
                  <Text>
                    {fee.computed_amount != null
                      ? `$${Number(fee.computed_amount).toFixed(2)}`
                      : fee.calculation_method === 'flat'
                        ? `$${Number(fee.value ?? 0).toFixed(2)}`
                        : `${Number(fee.value ?? 0)}% of ${String(fee.calculation_method || '').replace('percent_', '')}`}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        )}
        {inv.subtotal != null && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary">Subtotal</Text>
            <Text>${(inv.subtotal ?? 0).toFixed(2)}</Text>
          </Space>
        )}
        {/*
          The whole-job discount, between subtotal and fees exactly as it is
          applied. Without this line the page showed subtotal + fees + tax above
          its own total and named nothing for the gap.
        */}
        {Number(inv.discount_amount ?? 0) > 0 && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary">
              {inv.discount_type === 'percentage' && inv.discount_value
                ? `Discount (${inv.discount_value}%)`
                : 'Discount'}
            </Text>
            <Text>-${Number(inv.discount_amount ?? 0).toFixed(2)}</Text>
          </Space>
        )}
        {feesTotal > 0 && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary">Fees</Text>
            <Text>${feesTotal.toFixed(2)}</Text>
          </Space>
        )}
        {inv.tax_amount != null && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text type="secondary">Tax{inv.tax_rate != null ? ` (${inv.tax_rate}%)` : ''}</Text>
            <Text>${(inv.tax_amount ?? 0).toFixed(2)}</Text>
          </Space>
        )}
        <Space style={{ width: '100%', justifyContent: 'space-between', fontSize: 18 }}>
          <strong>Total</strong>
          <strong>${(inv.total_amount ?? 0).toFixed(2)}</strong>
        </Space>
        {!isPaid && (
          <Button type="primary" size="large" block loading={paying} onClick={onPay}>
            Pay Now — ${amountDue.toFixed(2)}
          </Button>
        )}

        {/*
          WP-10 slice 1 — the inspection this bill came out of: the note, the
          condition, the measurement and the photos, exactly as they appeared on
          the approval screen. Same component, so the two screens cannot say
          different things about the same car.

          Placed AFTER the pay button on purpose. This is a phone-first app and
          an inspection is commonly twenty-plus findings; putting the evidence
          above the button would bury the one action the screen exists for
          behind a long scroll. The findings are the appendix a customer reaches
          for when they want to check the charge, not a gate in front of paying.
        */}
        {data.inspection && <PortalDVISection dvi={data.inspection} />}
      </Space>
    </PortalLayout>
  );
};

export default PortalInvoicePage;
